import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ProjectHierarchy } from '../../api/protosCompiled/project/project_pb';
import { useCreateProjectHierarchy, useUpdateProjectHierarchy } from '../../hooks/useFetchProjects';

interface ProjectHierarchyDialogProps {
  open: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  parentId?: string | null;
  node?: ProjectHierarchy.AsObject;
  availableParents?: ProjectHierarchy.AsObject[];
  userId: string;
}

export const ProjectHierarchyDialog: React.FC<ProjectHierarchyDialogProps> = ({
  open,
  onClose,
  mode,
  parentId,
  node,
  availableParents = [],
  userId,
}) => {
  const theme = useTheme();
  const createMutation = useCreateProjectHierarchy(userId);
  const updateMutation = useUpdateProjectHierarchy(userId);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: parentId || '',
  });

  // Auto-generate code from name (all caps, alphanumeric + underscores only)
  const generateCode = (name: string): string => {
    return (
      name
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_|_$/g, '') || 'FOLDER'
    );
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form data when dialog opens or node changes
  useEffect(() => {
    if (mode === 'edit' && node) {
      setFormData({
        name: node.name || '',
        description: node.description || '',
        parentId: node.parentid || '',
      });
    } else {
      setFormData({
        name: '',
        description: '',
        parentId: parentId || '',
      });
    }
  }, [open, mode, node, parentId]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    try {
      if (mode === 'create') {
        const autoGeneratedCode = generateCode(formData.name.trim());
        await createMutation.mutateAsync({
          parentid: formData.parentId || '',
          code: autoGeneratedCode,
          name: formData.name.trim(),
          description: formData.description.trim(),
          isactive: true,
        });
      } else {
        // For update, we need to send all current values (PUT semantics)
        if (node) {
          await updateMutation.mutateAsync({
            id: node.id,
            parentid: formData.parentId || '',
            code: node.code, // Keep existing code when editing
            name: formData.name.trim(),
            description: formData.description.trim(),
            isactive: node.isactive,
          });
        }
      }

      onClose();
      setFormData({ name: '', description: '', parentId: '' });
    } catch (error) {
      console.error('Error saving hierarchy item:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setFormData({ name: '', description: '', parentId: '' });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6">
          {mode === 'create' ? 'Create New Folder' : 'Edit Folder'}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            disabled={isSubmitting}
            required
            sx={{ mb: 2 }}
          />

          <TextField
            margin="dense"
            label="Description (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={formData.description}
            onChange={e => handleInputChange('description', e.target.value)}
            disabled={isSubmitting}
            sx={{ mb: 2 }}
          />

          {mode === 'edit' && availableParents.length > 0 && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Parent Folder</InputLabel>
              <Select
                value={formData.parentId}
                onChange={e => handleInputChange('parentId', e.target.value)}
                disabled={isSubmitting}
                label="Parent Folder"
              >
                <MenuItem value="">
                  <em>Root Level</em>
                </MenuItem>
                {availableParents.map(parent => (
                  <MenuItem key={parent.id} value={parent.id}>
                    {parent.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1 }}>
        <Button onClick={handleClose} disabled={isSubmitting} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.name.trim()}
          variant="contained"
          color="primary"
        >
          {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
